import { SeoPageConfig } from "./seo-pages";

/** Посадочные страницы под запросы вида «<формат> в текст» */
export const FORMAT_PAGES: SeoPageConfig[] = [
  {
    path: "/wav-to-text",
    metaTitle: "Транскрибация WAV в текст онлайн | Расшифровка аудио без потерь",
    metaDescription:
      "Перевод WAV в текст с помощью ИИ. Несжатый звук даёт максимальную точность распознавания. Таймкоды, разделение по спикерам, экспорт в DOCX.",
    customTitle: "Транскрибация WAV в текст",
    customSubtitle: "Формат без потерь даёт лучший результат распознавания — загрузите запись как есть.",
    seoDescription: (
      <>
        <p>
          WAV хранит звук в исходном виде, без сжатия с потерями. Для распознавания речи это лучший из возможных
          исходников: модель получает всю звуковую картину целиком, включая тихие реплики и окончания слов, которые
          кодеки вроде MP3 частично отбрасывают. Разница особенно заметна на записях с несколькими собеседниками и
          заметным фоновым шумом.
        </p>
        <p>
          Обратная сторона формата — вес. Час стереозаписи в 44,1 кГц занимает около 600 МБ, поэтому длинные совещания в
          WAV легко упираются в лимит загрузки. Если файл не проходит, его достаточно один раз сохранить в MP3 с
          битрейтом 128 кбит/с и выше: на итоговой точности это почти не скажется, а размер уменьшится в несколько раз.
        </p>
        <p>
          Посчитать вес заранее несложно, потому что в несжатом звуке он линеен. Речевой стандарт — 16 кГц, 16 бит,
          моно — даёт около 1,9 МБ на минуту, то есть примерно 115 МБ в час. Студийные 44,1 кГц в стерео тяжелее почти
          в шесть раз. Отсюда простое правило: если запись длиннее двух часов и сделана в студийных параметрах,
          проверьте размер до загрузки.
        </p>
        <p>
          В WAV пишут профессиональные полевые рекордеры, аудиоинтерфейсы и программы монтажа звука — Audacity, Reaper,
          Adobe Audition. Именно поэтому формат чаще всего встречается там, где к записи изначально относились
          серьёзно: интервью для эфира, студийные подкасты, полевые исследования. На таких файлах распознавание
          показывает лучший результат, какой вообще возможен.
        </p>
        <p>
          Пара технических нюансов, о которых стоит знать. Некоторые рекордеры пишут в 32-битном формате с плавающей
          точкой — это удобно при монтаже, но для распознавания избыточно, и такой файл лучше пересохранить в обычные
          16 бит. Изредка попадаются WAV со сжатием ADPCM внутри: формально это тот же контейнер, но звук в нём уже с
          потерями, и преимущества перед MP3 у него нет.
        </p>
        <p>
          Если запись многодорожечная — например, каждый участник интервью писался на отдельный канал, — имеет смысл
          свести дорожки в одну перед загрузкой. Разделение по спикерам работает и так, но чистые раздельные каналы,
          сведённые в моно, дают более предсказуемый результат, чем стереофайл, где голоса разнесены по сторонам.
        </p>
      </>
    ),
    faq: [
      {
        question: "Почему WAV распознаётся точнее, чем MP3?",
        answer:
          "MP3 отбрасывает часть звуковой информации ради экономии места, и первыми страдают тихие звуки — концы слов, шёпот, реплики удалённого от микрофона собеседника. В WAV этих потерь нет, поэтому модели проще восстановить речь целиком."
      },
      {
        question: "WAV-файл слишком большой для загрузки, что делать?",
        answer:
          "Максимальный размер файла — 2 ГБ. Пересохраните запись в MP3 с битрейтом от 128 кбит/с: качество распознавания практически не изменится, а объём сократится примерно в десять раз."
      },
      {
        question: "Какие частота дискретизации и разрядность подойдут?",
        answer:
          "Достаточно 16 кГц и 16 бит — это стандарт для речи. Более высокие значения не дадут прироста точности, но увеличат размер файла."
      },
      {
        question: "Обрабатывается ли моно-запись хуже стерео?",
        answer:
          "Нет. Для распознавания речи моно вполне достаточно, а для диктофонных записей оно даже предпочтительнее — файл получается вдвое легче при том же результате."
      }
    ]
  },
  {
    path: "/mp4-to-text",
    metaTitle: "Транскрибация MP4 в текст онлайн | Расшифровка видео с таймкодами",
    metaDescription:
      "Расшифровка видео MP4 в текст без конвертации. ИИ извлекает аудиодорожку, распознаёт речь, размечает спикеров и отдаёт готовые субтитры SRT.",
    customTitle: "Транскрибация MP4 в текст",
    customSubtitle: "Загрузите видео напрямую — извлекать аудиодорожку вручную не нужно.",
    seoDescription: (
      <>
        <p>
          MP4 — самый распространённый видеоконтейнер: в нём сохраняют записи со смартфонов, экспортируют монтаж и
          выгружают вебинары. Отдельно вытаскивать звук не требуется, сервис сам находит аудиодорожку внутри контейнера
          и работает с ней, а видеоряд просто игнорирует.
        </p>
        <p>
          Каждая реплика получает таймкод, привязанный к оригинальному видео, поэтому по расшифровке удобно
          ориентироваться при монтаже: достаточно найти нужную фразу в тексте и перейти к соответствующей секунде.
          Результат выгружается в SRT — файл подходит для субтитров на YouTube, во «ВКонтакте» и в социальных сетях.
        </p>
        <p>
          Внутри MP4 звук почти всегда лежит в кодеке AAC с битрейтом 128–256 кбит/с — этого более чем достаточно для
          распознавания речи. Основной вес контейнера приходится на видеоряд, который при расшифровке не используется
          вообще. Отсюда практический вывод: если файл не проходит по лимиту в 2 ГБ, извлекать нужно именно звук, а не
          сжимать видео — размер сократится в десятки раз, а на точность это никак не повлияет.
        </p>
        <p>
          Чаще всего MP4 приходит из трёх источников: съёмка на смартфон, запись экрана через OBS или встроенные
          средства системы, и экспорт из видеоредактора. Записи экрана — самый удобный случай: звук в них идёт прямо с
          системного микшера или микрофона, без переотражений от стен, поэтому распознаются они очень чисто.
        </p>
        <p>
          Отдельная ситуация — видео, снятое на камеру в помещении с несколькими говорящими. Здесь качество определяет
          не формат, а расстояние до микрофона: камера в углу переговорной собирает эхо, и разделение по спикерам на
          такой записи работает заметно хуже, чем на созвоне, где каждый участник подключён со своей гарнитурой.
        </p>
        <p>
          Если исходник снят вертикально или в необычном разрешении, на расшифровку это не влияет никак: сервис
          обращается только к аудиопотоку, а параметры картинки, частота кадров и ориентация кадра для него не
          существуют.
        </p>
      </>
    ),
    faq: [
      {
        question: "Нужно ли извлекать аудио из MP4 перед загрузкой?",
        answer:
          "Нет. Загрузите видеофайл целиком — аудиодорожка будет извлечена автоматически, никаких конвертеров не потребуется."
      },
      {
        question: "Что будет, если в видео несколько аудиодорожек?",
        answer:
          "Обрабатывается первая дорожка в контейнере — обычно это основной звук. Если нужна другая, выделите её любым видеоредактором и загрузите отдельным файлом."
      },
      {
        question: "Можно ли получить субтитры для YouTube?",
        answer:
          "Да, результат выгружается в формате SRT с таймкодами. Этот файл загружается в YouTube, VK Video и Rutube как готовые субтитры."
      },
      {
        question: "Обязательно ли загружать файл, если видео уже опубликовано?",
        answer:
          "Нет. Если ролик выложен на YouTube, VK Video или Rutube, достаточно вставить ссылку — скачивать его не нужно."
      }
    ]
  },
  {
    path: "/mov-to-text",
    metaTitle: "Расшифровка MOV в текст | Транскрибация видео с iPhone и Mac",
    metaDescription:
      "Транскрибация видео MOV (QuickTime) в текст онлайн. Записи с iPhone, iPad и Mac обрабатываются напрямую, без конвертации в MP4.",
    customTitle: "Расшифровка MOV в текст",
    customSubtitle: "Видео с iPhone, iPad и Mac — загружайте в исходном формате QuickTime.",
    seoDescription: (
      <>
        <p>
          MOV — контейнер QuickTime, в который снимают все устройства Apple. Многие сервисы просят предварительно
          сконвертировать такие файлы в MP4, но здесь это лишний шаг: MOV обрабатывается напрямую, включая записи с
          высокой частотой кадров и в ProRes.
        </p>
        <p>
          Основная сложность с видео от iPhone — не формат, а вес. Съёмка в 4K занимает около 350 МБ на минуту, поэтому
          длинный ролик стоит выгружать через «Файлы» в сжатом виде либо предварительно экспортировать только звуковую
          дорожку: для расшифровки видеоряд всё равно не используется.
        </p>
        <p>
          Современные iPhone по умолчанию снимают в HEVC — этот кодек экономит место, но в некоторых старых
          редакторах открывается с трудом. Для расшифровки это неважно: сервис работает с аудиодорожкой, которая в MOV
          с камеры Apple лежит в обычном AAC и никаких сложностей не вызывает.
        </p>
        <p>
          Второй источник MOV — профессиональная съёмка и монтаж, где используется ProRes. Такие файлы очень тяжёлые:
          минута может занимать несколько гигабайт, и в лимит они не проходят практически никогда. Здесь единственный
          разумный путь — выгрузить из проекта только звук, например в M4A, и загрузить его.
        </p>
        <p>
          Типичный сценарий с MOV — интервью или комментарий, снятый на телефон «в поле»: журналистом, исследователем
          или на мероприятии. У таких записей своя особенность: телефон держат в руке, микрофон направлен не строго на
          говорящего, вокруг посторонние звуки. Речь в кадре при этом распознаётся хорошо, а вот реплики из-за кадра и
          с большого расстояния — заметно хуже.
        </p>
        <p>
          Если ролик снимался с внешним петличным микрофоном, качество будет ближе к студийному, и разница с обычной
          съёмкой на встроенный микрофон в тексте видна сразу — меньше пропущенных окончаний и точнее границы реплик.
        </p>
      </>
    ),
    faq: [
      {
        question: "Нужно ли конвертировать MOV в MP4?",
        answer: "Нет, MOV загружается напрямую. Конвертация не улучшит распознавание и только отнимет время."
      },
      {
        question: "Видео с iPhone весит слишком много — как быть?",
        answer:
          "При отправке через «Файлы» выберите вариант со сжатием либо экспортируйте из записи только аудио. Видеоряд при расшифровке не используется, поэтому на результат это не повлияет."
      },
      {
        question: "Работает ли сервис с записями экрана QuickTime?",
        answer:
          "Да. Записи экрана с Mac — типичный сценарий: из них удобно делать текстовые инструкции и конспекты демонстраций."
      }
    ]
  },
  {
    path: "/ogg-to-text",
    metaTitle: "OGG в текст онлайн | Расшифровка голосовых сообщений",
    metaDescription:
      "Расшифровка OGG в текст с помощью ИИ. Формат голосовых сообщений Telegram и WhatsApp распознаётся напрямую, без конвертации.",
    customTitle: "Расшифровка OGG в текст",
    customSubtitle: "Формат голосовых сообщений — переслушивать длинные аудио больше не нужно.",
    seoDescription: (
      <>
        <p>
          В OGG сохраняются голосовые сообщения Telegram и WhatsApp, а также записи из ряда диктофонов на Android. Если
          вам прислали десятиминутное аудио, а слушать его неудобно или некогда, расшифровка превращает его в текст,
          который читается за минуту и по которому работает поиск.
        </p>
        <p>
          Это открытый формат без лицензионных ограничений, и внутри него обычно лежит кодек Opus, изначально
          рассчитанный на речь. Несмотря на сильное сжатие, разборчивость голоса он сохраняет хорошо, поэтому точность
          расшифровки голосовых остаётся высокой.
        </p>
        <p>
          Внутри OGG может лежать не только Opus, но и более старый Vorbis — он встречается в записях с Android-устройств
          и в файлах, выгруженных из открытых аудиоредакторов. Для расшифровки разницы нет: сервис определяет кодек
          сам, и вмешиваться не требуется.
        </p>
        <p>
          Самый частый сценарий — переписка, где важное обсудили голосом. Несколько десятков сообщений подряд неудобно
          переслушивать, а в виде текста они читаются за пару минут и по ним можно искать. Голосовые сохраняются из
          мессенджера как обычные файлы и загружаются сюда без всякой подготовки.
        </p>
        <p>
          Особенность именно голосовых сообщений — короткая длина. Запись на десять-пятнадцать секунд расшифровывается
          так же, как часовая, но минимальная тарификация делает обработку множества коротких файлов по отдельности
          менее выгодной, чем одного длинного. Если сообщений много, имеет смысл склеить их в один файл.
        </p>
        <p>
          Качество голосовых обычно неплохое: человек говорит близко к микрофону телефона, что для распознавания
          выгоднее любой записи «из комнаты». Мешают скорее внешние обстоятельства — запись на ходу, ветер, шум улицы
          или транспорта.
        </p>
      </>
    ),
    faq: [
      {
        question: "Как расшифровать голосовое сообщение из Telegram?",
        answer:
          "Сохраните аудио из чата и загрузите файл на сайт. Либо отправьте голосовое напрямую нашему Telegram-боту — он вернёт текст ответным сообщением."
      },
      {
        question: "Чем OGG отличается от MP3?",
        answer:
          "OGG — открытый контейнер, чаще всего с кодеком Opus внутри, оптимизированным под голос. При одинаковом размере файла речь в нём звучит разборчивее, чем в MP3."
      },
      {
        question: "Распознаётся ли речь в шумной обстановке?",
        answer:
          "Как правило, да. Голосовые часто записывают на улице или в транспорте, и модель обучена отделять речь от фонового шума, хотя при очень громком фоне точность снижается."
      }
    ]
  },
  {
    path: "/opus-to-text",
    metaTitle: "Opus в текст | Расшифровка аудио в кодеке Opus онлайн",
    metaDescription:
      "Транскрибация файлов Opus в текст. Кодек голосовых сообщений и звонков распознаётся напрямую, с разделением по спикерам и таймкодами.",
    customTitle: "Расшифровка Opus в текст",
    customSubtitle: "Кодек голосовых сообщений и записей звонков — загружайте файл как есть.",
    seoDescription: (
      <>
        <p>
          Opus — современный кодек, на который перешли мессенджеры, браузерные звонки и WebRTC-сервисы. Он
          разрабатывался специально под передачу голоса в реальном времени, поэтому при крайне низком битрейте сохраняет
          разборчивость речи лучше универсальных кодеков.
        </p>
        <p>
          Для транскрибации это удачное сочетание: файлы весят немного, загружаются за секунды, а качества достаточно
          для точного распознавания. Чаще всего в Opus попадают записи звонков и совещаний из веб-сервисов — как раз тот
          материал, где важнее всего получить текст с разделением по участникам разговора.
        </p>
        <p>
          Экономичность Opus впечатляет: речь остаётся разборчивой уже на 16–24 кбит/с, то есть час разговора
          укладывается примерно в 10 МБ. Для сравнения, тот же час в несжатом WAV занял бы сотни мегабайт. Именно
          поэтому кодек выбрали для голосовых вызовов, где канал связи узкий и нестабильный.
        </p>
        <p>
          Важно понимать, что сжатие здесь необратимо. Если запись уже пришла в Opus, повышать её «качество»
          перекодированием в WAV бессмысленно — потерянная при сжатии информация не восстановится, а файл станет
          тяжелее в десятки раз. Загружайте исходник как есть, это оптимальный вариант.
        </p>
        <p>
          Отдельная сильная сторона кодека — устойчивость к потерям пакетов, из-за которых в записях звонков
          встречаются короткие провалы звука. Opus сглаживает их лучше старых кодеков, но если разрыв длинный, слово в
          этом месте просто отсутствует в записи — и восстановить его не сможет никакая модель.
        </p>
        <p>
          Для записей созвонов в Opus особенно полезны таймкоды: спорные фрагменты, где связь была плохой, легко
          находятся по тексту и переслушиваются точечно, без перемотки всей записи вслепую.
        </p>
      </>
    ),
    faq: [
      {
        question: "Opus и OGG — это одно и то же?",
        answer:
          "Не совсем. Opus — это кодек, то есть способ сжатия звука, а OGG — контейнер, файл-обёртка. Чаще всего Opus лежит именно в OGG, но встречается и в отдельных файлах с расширением .opus. Оба варианта обрабатываются одинаково."
      },
      {
        question: "Не теряется ли точность из-за сильного сжатия?",
        answer:
          "Opus сжимает звук с расчётом на сохранение речи, поэтому потери приходятся на то, что для распознавания не важно. На практике результат сопоставим с MP3 при вдвое меньшем размере файла."
      },
      {
        question: "Можно ли разделить реплики участников звонка?",
        answer:
          "Да, работает автоматическая диаризация: система определяет смену говорящего и помечает реплики как «Спикер 1», «Спикер 2» и так далее."
      }
    ]
  },
  {
    path: "/webm-to-text",
    metaTitle: "WEBM в текст онлайн | Расшифровка записей встреч и браузерного видео",
    metaDescription:
      "Транскрибация WEBM в текст. Записи из браузера, Google Meet и OBS расшифровываются напрямую — с таймкодами и разделением по спикерам.",
    customTitle: "Расшифровка WEBM в текст",
    customSubtitle: "Записи из браузера, онлайн-встреч и стримов — без предварительной конвертации.",
    seoDescription: (
      <>
        <p>
          WEBM — формат, в котором браузеры сохраняют результат записи экрана и звука. В нём выгружаются записи
          видеовстреч, ролики из веб-приложений и материалы, снятые через OBS. Файл загружается напрямую: перегонять его
          в MP4 не нужно.
        </p>
        <p>
          Типичный сценарий — часовая онлайн-встреча, к которой нужно вернуться за деталями. Расшифровка с таймкодами
          позволяет не пересматривать запись целиком: нужный фрагмент находится поиском по тексту, а функция AI-саммари
          собирает из обсуждения решения и договорённости.
        </p>
        <p>
          WEBM появился как формат для веба, и это определяет, откуда он берётся. Внутри почти всегда связка VP8 или
          VP9 для картинки и Opus для звука — то есть кодеки, оптимизированные под передачу через браузер в реальном
          времени. Для расшифровки важна только вторая половина этой связки, и она как раз рассчитана на речь.
        </p>
        <p>
          Чаще всего такие файлы получаются при записи прямо в браузере: расширения для захвата вкладки, встроенные
          средства записи в веб-сервисах, скачанные ролики с площадок, отдающих WEBM. Отдельный распространённый
          случай — запись экрана через браузерные инструменты во время созвона.
        </p>
        <p>
          У браузерной записи есть особенность, о которой стоит знать заранее: звук в неё попадает по-разному в
          зависимости от того, что именно захватывалось. Если писалась только вкладка, микрофон может не войти в файл
          вовсе — и тогда в расшифровке будут только удалённые собеседники, без вашего голоса.
        </p>
        <p>
          Проверить это проще всего до загрузки: откройте файл и послушайте несколько секунд из середины записи. Если
          слышны все участники, расшифровка будет полной; если кого-то не хватает, дело в настройках захвата, и никакая
          обработка это уже не исправит.
        </p>
      </>
    ),
    faq: [
      {
        question: "Откуда обычно берутся файлы WEBM?",
        answer:
          "Это стандартный результат записи экрана средствами браузера, выгрузки из веб-сервисов видеовстреч и работы OBS с настройками по умолчанию."
      },
      {
        question: "Нужно ли конвертировать WEBM в MP4?",
        answer: "Нет, формат обрабатывается напрямую вместе со звуковой дорожкой внутри контейнера."
      },
      {
        question: "Как быстро расшифруется запись часовой встречи?",
        answer: "Ориентировочно за 5–7 минут: обработка занимает около 10–15 % от длительности исходной записи."
      },
      {
        question: "Можно ли сразу получить краткое резюме встречи?",
        answer:
          "Да, после расшифровки доступна функция AI-саммари — она выделяет ключевые темы, принятые решения и задачи."
      }
    ]
  },
  {
    path: "/avi-to-text",
    metaTitle: "AVI в текст онлайн | Расшифровка архивных видеозаписей",
    metaDescription:
      "Транскрибация видео AVI в текст с помощью ИИ. Подходит для архивных записей лекций, семинаров и оцифрованных материалов.",
    customTitle: "Расшифровка AVI в текст",
    customSubtitle: "Архивные записи лекций и семинаров — старый формат обрабатывается наравне с современными.",
    seoDescription: (
      <>
        <p>
          AVI появился ещё в девяностые, но никуда не делся: в нём хранятся архивы лекций, записи с камер наблюдения и
          оцифрованные видеокассеты. Современные сервисы часто такие файлы уже не принимают, здесь же они загружаются
          наравне с MP4.
        </p>
        <p>
          У архивных записей своя специфика — шум, неровная громкость, эхо в аудитории. Полностью это не компенсировать,
          но модель обучена работать с неидеальным звуком, поэтому текст получается пригодным для чтения и поиска. Если
          материал критичен, стоит выбрать режим повышенной точности.
        </p>
        <p>
          AVI — контейнер из девяностых, и сегодня он встречается почти исключительно в архивах. Это его главная
          особенность с точки зрения расшифровки: файлы в AVI обычно старые, а значит записаны на технику своего
          времени — кассетные камеры, ранние диктофоны, оцифровку с плёнки.
        </p>
        <p>
          Отсюда типичные проблемы такого материала: узкий частотный диапазон, шипение ленты, наводки и общий низкий
          уровень записи. Современная модель распознавания справляется с этим лучше, чем можно ожидать, но рассчитывать
          на ту же точность, что на чистой цифровой записи, не стоит.
        </p>
        <p>
          Внутри AVI может лежать почти что угодно: звук встречается и в MP3, и в старом ADPCM, и в несжатом PCM.
          Сервис разбирается с этим сам, отдельно определять кодек не нужно. Изредка попадаются файлы с экзотическим
          кодеком, который не читается, — в этом случае поможет пересохранение в MP3 любым конвертером.
        </p>
        <p>
          Для архивных записей особенно полезно то, что расшифровка делает их доступными для поиска. Часы семейного
          видео, старых интервью или записей мероприятий превращаются в текст, по которому можно найти нужный момент, —
          а именно этого архивам обычно и не хватает.
        </p>
      </>
    ),
    faq: [
      {
        question: "Обрабатываются ли старые видеофайлы?",
        answer:
          "Да. Возраст файла значения не имеет: важна только звуковая дорожка внутри контейнера, а её формат определяется автоматически."
      },
      {
        question: "Запись очень шумная, будет ли толк?",
        answer:
          "Текст получится, но точность будет ниже, чем на чистом звуке. Для шумных архивов лучше выбирать режим «Качество» — он заметно устойчивее к помехам."
      },
      {
        question: "Что делать, если файл весит больше 2 ГБ?",
        answer:
          "Извлеките из видео только аудиодорожку и загрузите её отдельно. Для расшифровки видеоряд не нужен, а размер сократится в десятки раз."
      }
    ]
  },
  {
    path: "/mkv-to-text",
    metaTitle: "MKV в текст онлайн | Расшифровка длинных видеозаписей",
    metaDescription:
      "Транскрибация MKV (Matroska) в текст. Формат для длинных записей — стримов, вебинаров и полных семинаров с таймкодами.",
    customTitle: "Расшифровка MKV в текст",
    customSubtitle: "Длинные стримы, вебинары и многочасовые записи — с навигацией по таймкодам.",
    seoDescription: (
      <>
        <p>
          Matroska (MKV) выбирают там, где запись длинная: стримы, полные семинары, многочасовые вебинары. Контейнер не
          ограничивает ни длительность, ни количество дорожек, поэтому в него часто складывают запись целиком, ничего не
          нарезая.
        </p>
        <p>
          Для таких материалов ценность расшифровки максимальна: пересматривать трёхчасовую запись ради одного тезиса
          никто не станет, а по тексту с таймкодами нужное место находится за секунды. Ограничение одно — максимальная
          длительность обработки составляет четыре часа, более длинные записи стоит разбить на части.
        </p>
        <p>
          Главная особенность Matroska — он умеет хранить сколько угодно дорожек в одном файле. Это удобно для фильмов
          с несколькими языками озвучки, но для расшифровки создаёт неоднозначность: если аудиодорожек несколько,
          обрабатывается первая в контейнере, а она не всегда та, которая нужна.
        </p>
        <p>
          Проверить состав файла можно любым плеером, который показывает список дорожек. Если нужная озвучка идёт не
          первой, выделите её отдельно и загрузите как самостоятельный аудиофайл — это надёжнее, чем рассчитывать на
          порядок дорожек внутри контейнера.
        </p>
        <p>
          Откуда обычно берутся MKV: записи стримов, длинные трансляции, сохранённые эфиры и материалы, собранные из
          нескольких источников. Такие файлы часто очень длинные — многочасовая трансляция в лимит по длительности не
          проходит, и её приходится резать на части.
        </p>
        <p>
          Резать имеет смысл по смыслу, а не механически по времени: если разбить запись посередине фразы, на стыке
          частей появится оборванное предложение. Пауза между темами или смена говорящего — гораздо более удачное место
          для разреза.
        </p>
      </>
    ),
    faq: [
      {
        question: "Какая максимальная длительность записи?",
        answer: "До четырёх часов на файл. Более длинную запись нужно разделить на части и загрузить их по очереди."
      },
      {
        question: "В MKV несколько звуковых дорожек — какая будет обработана?",
        answer:
          "Берётся первая дорожка в контейнере. Если нужна другая, например с переводом, выделите её отдельным файлом перед загрузкой."
      },
      {
        question: "Сколько времени займёт расшифровка трёхчасового стрима?",
        answer: "Примерно 20–25 минут. Обработка занимает около 10–15 % от длительности исходной записи."
      }
    ]
  },
  {
    path: "/aac-to-text",
    metaTitle: "AAC в текст онлайн | Расшифровка аудио в формате AAC",
    metaDescription:
      "Транскрибация AAC в текст с помощью ИИ. Стандарт звука для стриминга и устройств Apple обрабатывается без конвертации.",
    customTitle: "Расшифровка AAC в текст",
    customSubtitle: "Стандарт звука для стриминга и подкастов — загружайте файл напрямую.",
    seoDescription: (
      <>
        <p>
          AAC пришёл на смену MP3 и стал стандартом де-факто: в нём звучит аудиодорожка YouTube, работают Apple Music и
          большинство подкаст-платформ. При том же размере файла он сохраняет больше деталей, чем MP3, — для
          распознавания речи это небольшой, но приятный плюс.
        </p>
        <p>
          Чаще всего в AAC попадают подкасты и выгрузки звука из видео. Для таких записей особенно полезно разделение по
          спикерам: диалог ведущего и гостя размечается автоматически, и из расшифровки получается готовая основа для
          статьи или описания выпуска.
        </p>
        <p>
          AAC пришёл на смену MP3 и при том же битрейте звучит заметно лучше — особенно на верхних частотах, где MP3
          заметно грубит. Для распознавания речи это небольшое, но реальное преимущество: согласные вроде «с», «ш» и
          «ф» живут как раз в верхнем диапазоне, и именно на них чаще всего спотыкаются модели.
        </p>
        <p>
          Встречается формат повсюду, где есть техника Apple и потоковое вещание: диктофон на iPhone, звук внутри
          видеофайлов, подкасты, аудио из телетрансляций. Часто AAC приходит не отдельным файлом, а дорожкой внутри
          MP4 или MOV — в этом случае извлекать её вручную не нужно.
        </p>
        <p>
          Практическое следствие из хорошего сжатия: файлы в AAC компактные, и в лимит по размеру они упираются редко.
          Час записи на типичном битрейте занимает порядка 50–60 МБ, так что даже длинная конференция помещается в
          лимит без всякой подготовки.
        </p>
        <p>
          Единственное, чего не стоит делать, — перекодировать AAC в другой формат «для надёжности». Любая
          перекодировка сжатого звука добавляет потери поверх уже имеющихся, и результат окажется хуже исходника.
          Загружайте файл в том виде, в каком он есть.
        </p>
      </>
    ),
    faq: [
      {
        question: "Чем AAC лучше MP3 для распознавания?",
        answer:
          "При равном размере файла AAC сохраняет больше высоких частот, от которых зависит различимость согласных. На практике разница невелика, но она в пользу AAC."
      },
      {
        question: "Файл имеет расширение .m4a — это AAC?",
        answer:
          "Как правило, да: .m4a — это контейнер, внутри которого лежит AAC. Загружается он точно так же, без всякой подготовки."
      },
      {
        question: "Можно ли разделить реплики ведущего и гостя?",
        answer:
          "Да, диаризация включена по умолчанию: система сама определяет смену говорящего и помечает каждую реплику."
      }
    ]
  },
  {
    path: "/wma-to-text",
    metaTitle: "WMA в текст онлайн | Расшифровка записей с диктофонов Windows",
    metaDescription:
      "Транскрибация WMA в текст. Формат Windows Media с диктофонов и корпоративных архивов распознаётся без конвертации.",
    customTitle: "Расшифровка WMA в текст",
    customSubtitle: "Записи с диктофонов и корпоративных архивов Windows — без поиска конвертера.",
    seoDescription: (
      <>
        <p>
          WMA — формат Microsoft, в котором записывали звук диктофоны прошлых лет и стандартные средства Windows.
          Сегодня он встречается в основном в архивах: старых интервью, записях совещаний, корпоративных фонотеках.
          Многие онлайн-сервисы его уже не принимают, из-за чего приходится искать конвертер.
        </p>
        <p>
          Здесь этот шаг не нужен — файл загружается напрямую. Для архивных записей стоит иметь в виду, что диктофоны
          тех лет писали с невысоким битрейтом, поэтому на сложных фрагментах точность будет ниже, чем на современной
          записи. Режим повышенной точности в таких случаях даёт заметно более чистый результат.
        </p>
        <p>
          WMA — формат Microsoft, и почти весь материал в нём достался нам от эпохи Windows XP: диктофоны тех лет,
          стандартная звукозапись системы, оцифровки, сделанные встроенными средствами. Сегодня новые записи в WMA
          практически не появляются, зато старых архивов накопилось много.
        </p>
        <p>
          Именно поэтому формат хуже всего поддерживается онлайн-сервисами: разработчики не видят смысла возиться с
          устаревшим кодеком. Здесь он принимается наравне с остальными, и промежуточная конвертация не требуется —
          что для архивных записей избавляет от лишнего шага и лишней потери качества.
        </p>
        <p>
          Чего ожидать по точности: записи тех лет часто делались на низком битрейте, в моно и с посредственным
          микрофоном. Речь при этом остаётся разборчивой, но окончания слов и тихие реплики теряются чаще, чем на
          современной цифровой записи. Текст стоит рассматривать как черновик и вычитывать.
        </p>
        <p>
          Практический совет для архивов: если файлов много, начните с одного пробного. По нему сразу станет понятно,
          какого качества ожидать от всей коллекции, — записи из одного источника обычно ведут себя одинаково.
        </p>
      </>
    ),
    faq: [
      {
        question: "Нужно ли конвертировать WMA в MP3?",
        answer: "Нет, файл обрабатывается напрямую. Конвертация не добавит качества — оно ограничено исходной записью."
      },
      {
        question: "Насколько точно распознаются старые диктофонные записи?",
        answer:
          "Зависит от исходника. При разборчивой речи результат остаётся высоким, при сильном шуме и низком битрейте точность снижается — в таких случаях помогает режим «Качество»."
      },
      {
        question: "Поддерживается ли WMA с защитой DRM?",
        answer: "Нет. Файлы с защитой от копирования не читаются — потребуется исходная версия записи без ограничений."
      }
    ]
  }
];
